import React from "react";
import Image from "next/image";
import { ImageDataType } from "../types";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { displayFontClassName } from "@/lib/fonts";
import { cn } from "@/lib/utils";

export type FeatureCardVariant = "with_image" | "with_symbol" | "no_icon";

export const DEFAULT_VARIANT: FeatureCardVariant = "with_symbol";

export type FeatureCardTheme = {
  headline: string;
  bodyText: string;
  symbolBorder: string;
  symbolText: string;
};

export const defaultFeatureCardTheme: FeatureCardTheme = {
  headline: "text-foreground",
  bodyText: "text-tag-foreground",
  symbolBorder: "border-primary",
  symbolText: "text-primary",
};

export interface FeatureCardCommonInterface {
  variant?: FeatureCardVariant;
  headline: string;
  description?: string;
  image?: ImageDataType | null;
  symbol?: string;
}

interface FeatureCardProps extends FeatureCardCommonInterface {
  theme?: FeatureCardTheme;
  descriptionClassName?: string;
}

const cardPaddingClass = "px-4 py-10 md:px-6 md:py-10 desktop:px-10 desktop:py-20";

const descriptionProseClass =
  "font-sans text-base leading-[1.2] [&_a]:text-link [&_li]:mt-0 [&_ol]:list-decimal [&_ol]:pl-6 [&_p+p]:mt-0 [&_ul]:list-disc [&_ul]:pl-6";

export function FeatureCard({
  headline,
  description,
  variant = DEFAULT_VARIANT,
  image,
  symbol,
  theme = defaultFeatureCardTheme,
  descriptionClassName,
}: FeatureCardProps) {
  const resolvedDescriptionClassName = cn(
    descriptionProseClass,
    theme.bodyText,
    descriptionClassName
  );
  const headlineClassName = cn(
    displayFontClassName,
    "text-heading-3-mobile font-bold leading-[1.2] md:text-heading-3",
    theme.headline
  );

  const renderSymbol = () => (
    <span
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-pill border-2",
        theme.symbolBorder,
        theme.symbolText,
        "font-sans text-xl font-bold leading-none"
      )}
      aria-hidden={!symbol}
    >
      {symbol || "!"}
    </span>
  );

  const renderTextBlock = () => (
    <div className="flex min-w-0 flex-col gap-4">
      {headline && <p className={headlineClassName}>{headline}</p>}
      {description && (
        <MarkdownRenderer
          content={description}
          className={resolvedDescriptionClassName}
        />
      )}
    </div>
  );

  if (variant === "with_symbol") {
    return (
      <div className={cn("flex items-start gap-6", cardPaddingClass)}>
        {renderSymbol()}
        {renderTextBlock()}
      </div>
    );
  }

  if (variant === "with_image" && image) {
    return (
      <div className={cn("flex flex-col items-start gap-4", cardPaddingClass)}>
        <div className="relative size-16 shrink-0">
          <Image
            src={image.src}
            alt={image.alt || headline || ""}
            title={image.title ?? undefined}
            width={64}
            height={64}
            className="size-16 object-contain"
          />
        </div>
        {renderTextBlock()}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-start gap-4", cardPaddingClass)}>
      {renderTextBlock()}
    </div>
  );
}
